import { useState } from 'react'
import type { ReactElement } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import type { MemoryDocumentRow } from '../lib/content-api.ts'
import {
  createMemoryDocument,
  deleteMemoryDocument,
  listMemoryDocuments,
  patchMemoryDocument,
} from '../lib/content-api.ts'
import { listAlerts } from '../lib/alerts-api.ts'
import { MemoryConflictPanel } from '../components/MemoryConflictPanel.tsx'
import {
  MemoryDocumentPanel,
  type MemoryCreateFields,
} from '../components/MemoryDocumentPanel.tsx'
import {
  Button,
  Callout,
  EmptyState,
  PageHeader,
} from '../components/ui/index.ts'

export const Route = createFileRoute('/memory')({
  loader: async () =>
    withSessionRedirect(async () => {
      const [rows, openAlerts] = await Promise.all([
        listMemoryDocuments(),
        listAlerts('open'),
      ])
      const conflictAlerts = openAlerts.filter(
        (a) => a.kind === 'memory_conflict',
      )
      return { rows, conflictAlerts }
    }),
  component: MemoryRoute,
})

type Panel =
  | { mode: 'create' }
  | { mode: 'edit'; row: MemoryDocumentRow }
  | { mode: 'conflict'; docId: string }
  | null

const emptyCreate: MemoryCreateFields = {
  rootId: '',
  scope: 'org_node',
  refId: '',
  name: 'memory',
  markdown: '',
}

function MemoryRoute(): ReactElement {
  const { rows, conflictAlerts } = Route.useLoaderData()
  const router = useRouter()

  const [panel, setPanel] = useState<Panel>(null)
  const [createFields, setCreateFields] = useState(emptyCreate)
  const [editName, setEditName] = useState('')
  const [editMarkdown, setEditMarkdown] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function startCreate() {
    setPanel({ mode: 'create' })
    setCreateFields(emptyCreate)
    setError(null)
  }

  function startEdit(row: MemoryDocumentRow) {
    setPanel({ mode: 'edit', row })
    setEditName(row.name)
    setEditMarkdown(row.markdown)
    setError(null)
  }

  function startConflict(docId: string) {
    setPanel({ mode: 'conflict', docId })
    setError(null)
  }

  async function refresh() {
    await router.invalidate()
  }

  async function handleSave() {
    if (!panel) return
    setSaving(true)
    setError(null)
    try {
      if (panel.mode === 'create') {
        await createMemoryDocument({ ...createFields })
      } else if (panel.mode === 'edit') {
        await patchMemoryDocument(panel.row.id, {
          name: editName,
          markdown: editMarkdown,
        })
      }
      setPanel(null)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (panel?.mode !== 'edit') return
    setSaving(true)
    setError(null)
    try {
      await deleteMemoryDocument(panel.row.id)
      setPanel(null)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Memory documents"
        description="The markdown each scope hands its sessions. A harness pushing a stale copy raises a conflict."
        actions={
          // The same words as the empty state's action below. They are the
          // same action, and `+ add` here against `New document` there made
          // one page look like two.
          <Button variant="primary" onClick={startCreate}>
            New document
          </Button>
        }
      />

      <div className="flex gap-8">
        <div className="w-80 shrink-0">
          {conflictAlerts.length > 0 && (
            <div className="mb-4">
              {/* `danger`, not `warn`: a conflict means an agent's write was
                  refused and its copy is only alive in the event trail
                  until someone picks a side. */}
              <Callout
                tone="danger"
                title={`Conflicts (${String(conflictAlerts.length)})`}
              >
                <ul className="flex flex-col gap-1">
                  {conflictAlerts.map((alert) => (
                    <li key={alert.id}>
                      <button
                        type="button"
                        className="rounded-sm text-left underline-offset-2 transition-colors hover:underline"
                        onClick={() => {
                          startConflict(alert.entityId)
                        }}
                      >
                        {alert.message}
                      </button>
                    </li>
                  ))}
                </ul>
              </Callout>
            </div>
          )}

          <nav aria-label="Memory documents">
            <ul className="flex flex-col gap-0.5">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    aria-current={
                      panel?.mode === 'edit' && panel.row.id === row.id
                        ? 'true'
                        : undefined
                    }
                    className={`w-full rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-elevated ${
                      panel?.mode === 'edit' && panel.row.id === row.id
                        ? 'bg-elevated text-fg'
                        : 'text-muted'
                    }`}
                    onClick={() => {
                      startEdit(row)
                    }}
                  >
                    <span className="block truncate text-fg">{row.name}</span>
                    <span className="block truncate text-xs text-faint">
                      {row.scope}/{row.refId} (v{row.version})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {rows.length === 0 && (
            <EmptyState
              headline="No memory documents"
              description="A memory document is the markdown a scope hands every session it starts."
              action={
                <Button variant="primary" onClick={startCreate}>
                  New document
                </Button>
              }
            />
          )}
        </div>

        {panel?.mode === 'conflict' ? (
          <MemoryConflictPanel
            docId={panel.docId}
            saving={saving}
            setSaving={setSaving}
            error={error}
            setError={setError}
            onClose={() => {
              setPanel(null)
            }}
            onResolved={refresh}
          />
        ) : (
          panel && (
            <MemoryDocumentPanel
              mode={panel.mode}
              row={panel.mode === 'edit' ? panel.row : null}
              createFields={createFields}
              onCreateFieldsChange={setCreateFields}
              editName={editName}
              onEditNameChange={setEditName}
              editMarkdown={editMarkdown}
              onEditMarkdownChange={setEditMarkdown}
              saving={saving}
              error={error}
              onSave={() => void handleSave()}
              onDelete={() => void handleDelete()}
            />
          )
        )}
      </div>
    </div>
  )
}
